// src/components/modules/file-manager/FilesHeader.tsx
import {
  faAdd,
  faArrowsRotate,
  faBars,
  faCloudArrowUp,
  faFilter,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import PhoenixOffcanvas from 'components/base/PhoenixOffcanvas';
import { useBreakpoints } from 'providers/BreakpointsProvider';
import React, { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { Form, Row, Col, Modal } from 'react-bootstrap';
import Sidebar from './sidebar/Sidebar';
import SearchBox from 'components/common/SearchBox';
import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';
import axios from 'axios';

interface File {
  id?: string;
  name: string;
  type?: string;
  size?: number;
  date?: string;
}

interface FileHeaderProps {
  showSidebar: boolean;
  setShowSidebar: Dispatch<SetStateAction<boolean>>;
  openOffcanvas: boolean;
  setOpenOffcanvas: Dispatch<SetStateAction<boolean>>;
}

const FilesHeader = ({
  showSidebar,
  setShowSidebar,
  openOffcanvas,
  setOpenOffcanvas,
}: FileHeaderProps) => {
  const { breakpoints } = useBreakpoints();
  const [addFolderModal, setAddFolderModal] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const { setGlobalFilter } = useAdvanceTableContext<File>();
  const [fileType, setFileType] = useState('');
  const [files, setFiles] = useState<FileList | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, chunk: '' });
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000';

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGlobalFilter(e.target.value || undefined);
  };

  const handleFileTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setFileType(e.target.value);
    setMessage('');
    setProgress({ current: 0, total: 0, chunk: '' });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      console.log('Selected files:', Array.from(e.target.files).map(f => f.name));
      setFiles(e.target.files);
      setMessage('');
      setProgress({ current: 0, total: 0, chunk: '' });
    }
  };

  const splitFile = (file: File, maxLines: number = 11000): Promise<File[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        if (!text) {
          reject(new Error('No se pudo leer el archivo'));
          return;
        }
        const lines = text.split('\n');
        const chunks: File[] = [];
        let currentChunk: string[] = [];
        let lineCount = 0;

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (
            i + 1 < lines.length &&
            lines[i + 1].trim().startsWith('Procesalegui:') &&
            lineCount >= maxLines - 1000
          ) {
            chunks.push(
              new File([currentChunk.join('\n')], `${file.name}_part${chunks.length + 1}.txt`, {
                type: 'text/plain',
              })
            );
            currentChunk = [];
            lineCount = 0;
          }
          currentChunk.push(lines[i]);
          lineCount++;

          if (lineCount >= maxLines && !line.startsWith('Procesalegui:')) {
            chunks.push(
              new File([currentChunk.join('\n')], `${file.name}_part${chunks.length + 1}.txt`, {
                type: 'text/plain',
              })
            );
            currentChunk = [];
            lineCount = 0;
          }
        }

        if (currentChunk.length > 0) {
          chunks.push(
            new File([currentChunk.join('\n')], `${file.name}_part${chunks.length + 1}.txt`, {
              type: 'text/plain',
            })
          );
        }

        console.log(`Split ${file.name} into ${chunks.length} chunks`);
        resolve(chunks);
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsText(file as unknown as Blob); // Double cast to fix TS2352
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submitting form with fileType:', fileType);
    console.log('Files:', files ? Array.from(files).map(f => f.name) : 'none');

    if (!fileType) {
      setMessage('Por favor, seleccione el tipo de archivo');
      return;
    }
    if (!files || files.length === 0) {
      setMessage('Por favor, seleccione al menos un archivo');
      return;
    }

    const maxFileSize = 100 * 1024 * 1024; // 100MB
    let validFiles = true;
    const filesToUpload: File[] = [];

    for (const file of Array.from(files)) {
      console.log('Processing file:', file.name, 'size:', file.size);
      if (!file.name.match(/\.(csv|txt|TXT)$/i)) {
        setMessage('Solo se permiten archivos .csv o .txt');
        validFiles = false;
        break;
      }
      if (file.size > maxFileSize) {
        setMessage('El archivo es demasiado grande (máximo 100MB)');
        validFiles = false;
        break;
      }
      if (fileType === 'mtos' && file.name.match(/\.(txt|TXT)$/i)) {
        try {
          const chunks = await splitFile(file);
          filesToUpload.push(...chunks);
        } catch (error) {
          setMessage(`Error al dividir el archivo: ${error || 'Error desconocido'}`);
          validFiles = false;
          break;
        }
      } else {
        filesToUpload.push(file);
      }
    }

    if (!validFiles) return;

    setLoading(true);
    setProgress({ current: 0, total: filesToUpload.length, chunk: '' });

    let totalInserted = 0;
    let totalUpdated = 0;
    let totalSkipped = 0;
    let totalRecords = 0;

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      setProgress({ current: i + 1, total: filesToUpload.length, chunk: file.name });

      const formData = new FormData();
      formData.append('file', file as unknown as Blob, file.name); // Double cast to fix TS2345

      try {
        const endpoint = `${apiUrl}/uploads/${fileType.toLowerCase()}`;
        console.log(`Uploading chunk ${i + 1}/${filesToUpload.length}: ${file.name} to ${endpoint}`);
        const response = await axios.post(endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            console.log(`Upload progress for ${file.name}: ${percent}%`);
          },
        });
        const { inserted, updated, skipped, total } = response.data;
        totalInserted += inserted || 0;
        totalUpdated += updated || 0;
        totalSkipped += skipped || 0;
        totalRecords += total || 0;
      } catch (error) {
        console.error(`Upload error for ${file.name}:`, error);
        setMessage(`Error al subir ${file.name}: ${error || 'No se pudo procesar el archivo'}`);
        setLoading(false);
        return;
      }
    }

    setLoading(false);
    setFiles(null);
    const input = document.getElementById('fileUpload') as HTMLInputElement;
    if (input) input.value = '';
    setProgress({ current: 0, total: 0, chunk: '' });
    setMessage(
      `Éxito: ${totalInserted} filas insertadas, ${totalUpdated} actualizadas, ${totalSkipped} omitidas de ${totalRecords} totales`
    );
  };

  return (
    <>
      <Row className="g-3 flex-between-center">
        <Col xs="auto" className="d-flex gap-2">
          <Button
            variant="phoenix-secondary"
            className="btn-square d-xl-none"
            onClick={() => {
              setOpenOffcanvas(true);
              setShowSidebar(!showSidebar);
            }}
          >
            <FontAwesomeIcon icon={faBars} />
          </Button>

          <Form onSubmit={handleSubmit}>
            <div className="d-flex align-items-center gap-2">
              <Form.Select
                value={fileType}
                onChange={handleFileTypeChange}
                className="w-auto"
              >
                <option value="">Seleccione tipo de archivo</option>
                <option value="proc">Tabla Proc (Lex)</option>
                <option value="cb">CB, Acto, Dema, Obse, Carpe</option>
                <option value="mtos">Historias (Mtos)</option>
              </Form.Select>

              <input
                type="file"
                id="fileUpload"
                className="d-none"
                accept=".csv,.txt"
                multiple
                onChange={handleFileChange}
              />
              <label htmlFor="fileUpload">
                <Button
                  as="span"
                  variant="primary"
                  className="px-sm-6"
                  disabled={loading || !fileType}
                >
                  <FontAwesomeIcon icon={faCloudArrowUp} className="me-2" />
                  {loading ? 'Subiendo...' : 'Seleccionar Archivo'}
                </Button>
              </label>

              <Button
                type="submit"
                variant="primary"
                className="px-sm-6"
                disabled={loading || !fileType || !files}
              >
                <FontAwesomeIcon icon={faCloudArrowUp} className="me-2" />
                {loading ? 'Enviando...' : 'Enviar'}
              </Button>
            </div>
          </Form>

          <Button
            variant="link"
            className="text-body-secondary px-2"
            onClick={() => setAddFolderModal(true)}
          >
            <FontAwesomeIcon icon={faAdd} className="me-2" />
            Agregar Nueva Carpeta
          </Button>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <SearchBox
            onChange={handleSearchInputChange}
            placeholder="Buscar por nombre"
          />
          <Form.Select className="w-auto">
            <option value="1">Últimos 7 días</option>
            <option value="2">Últimos 15 días</option>
            <option value="3">Últimos 30 días</option>
          </Form.Select>
          <Button
            variant="phoenix-primary"
            onClick={() => setFilterModal(true)}
            className="btn-square"
          >
            <FontAwesomeIcon icon={faFilter} />
          </Button>
        </Col>
      </Row>
      {message && <p className="mt-3">{message}</p>}
      {progress.total > 0 && (
        <div className="mt-3">
          <p>Procesando chunk {progress.current} de {progress.total}: {progress.chunk}</p>
          <progress value={progress.current} max={progress.total} />
        </div>
      )}
      {breakpoints.down('lg') && (
        <PhoenixOffcanvas
          open={openOffcanvas}
          onHide={() => setOpenOffcanvas(false)}
          className="phoenix-offcanvas treeview-offcanvas phoenix-offcanvas-content scrollbar bg-body overflow-x-hidden"
          placement="start"
          fixed
        >
          <Sidebar setOpenOffcanvas={setOpenOffcanvas} />
        </PhoenixOffcanvas>
      )}
      <Modal
        show={addFolderModal}
        onHide={() => setAddFolderModal(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header className="border-0">
          <Modal.Title id="contained-modal-title-vcenter">
            Agregar nueva carpeta
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <Form>
            <Form.Control type="search" placeholder="Nombre de la carpeta" />
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="link"
            className="text-danger me-0"
            onClick={() => setAddFolderModal(false)}
          >
            Cancelar
          </Button>
          <Button variant="primary" size="sm" className="px-5 ms-0">
            Aplicar
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={filterModal}
        onHide={() => setFilterModal(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header className="border-0">
          <h5 className="modal-title">Filtro</h5>
          <Button
            variant="link"
            className="ms-auto"
            onClick={() => setFilterModal(false)}
            size="sm"
          >
            <FontAwesomeIcon icon={faTimes} className="text-danger" />
          </Button>
        </Modal.Header>
        <Modal.Body className="py-2">
          <Form>
            <Form.Group controlId="selectType" className="mb-3">
              <Form.Label className="form-label-header mb-1">Tipo</Form.Label>
              <Form.Select name="selectType">
                <option>Seleccionar</option>
                <option value="1">Documentos</option>
                <option value="2">Imágenes</option>
                <option value="3">Videos</option>
                <option value="4">Audios</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="selectPeople" className="mb-3">
              <Form.Label className="form-label-header mb-1">Personas</Form.Label>
              <Form.Select name="selectPeople">
                <option>Seleccionar</option>
                <option value="1">Robert Allan</option>
                <option value="2">Charles</option>
                <option value="3">Adrian</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="lastModified" className="mb-3">
              <Form.Label className="form-label-header mb-1">
                Modificado
              </Form.Label>
              <Form.Select name="lastModified">
                <option>Seleccionar</option>
                <option value="today">Hoy</option>
                <option value="last7Days">Últimos 7 días</option>
                <option value="last15Days">Últimos 15 días</option>
                <option value="last30Days">Últimos 30 días</option>
                <option value="chooseATimePeriod">Elegir un período</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="location" className="mb-3">
              <Form.Label className="form-label-header mb-1">
                Ubicación
              </Form.Label>
              <Form.Select name="location">
                <option>Seleccionar</option>
                <option value="1">Cualquier lugar en el disco</option>
                <option value="2">Compartido conmigo</option>
                <option value="3">Destacado</option>
                <option value="4">Papelera</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="link"
            className="text-body-secondary px-3 mx-0"
            onClick={() => setFilterModal(false)}
          >
            <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
            Restablecer
          </Button>
          <Button variant="primary" size="sm" className="px-5 ms-0">
            Aplicar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FilesHeader;